import React, { useEffect, useState } from 'react';
import { getPage } from '../config/pages';
import { useTranslation } from '../services/translationService';
import './AppShell.css';

const ICONS = {
  fields: 'M4 8l7-4 9 3-2 11-10 3-5-6z',
  parcels: 'M7 3h7l5 5v13H7zM14 3v5h5',
  arendamanager: 'M12 21V9M12 9c0-3 2-5 5-5 0 3-2 5-5 5zM12 14c0-3-2-5-5-5 0 3 2 5 5 5zM12 19c0-3 2-5 5-5 0 3-2 5-5 5z',
  stockadjustment: 'M3 8l9-5 9 5v9l-9 5-9-5zM3 8l9 5 9-5M12 13v9',
  motorina: 'M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M3 21h14M8 8h4M15 12h2a2 2 0 012 2v3a1.5 1.5 0 003 0V9l-3-3',
  equipment: 'M7 21a3 3 0 100-6 3 3 0 000 6zM18 21a2 2 0 100-4 2 2 0 000 4zM4 15V8h8l3 5h4v5M7 8V4h4v4',
  settings: 'M4 7h10M18 7h2M4 17h2M10 17h10M16 5v4M8 15v4',
  more: 'M4 6h16M4 12h16M4 18h16',
  install: 'M12 4v11M7 11l5 5 5-5M4 20h16',
  logout: 'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
  fallback: 'M12 5a7 7 0 100 14 7 7 0 000-14z',
};

const Icon = ({ name, size = 21 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
    <path d={ICONS[name] || ICONS.fallback} />
  </svg>
);

const isInstalled = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true ||
  Boolean(window.Capacitor?.isNativePlatform?.());

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const BUNDLE_PATTERN = /static\/js\/main\.[a-z0-9]+\.js/;
const VERSION_CHECK_EVERY_MS = 5 * 60 * 1000;

// Asks the server which version is live and compares it with the one running here.
export const isNewVersionLive = async () => {
  const running = [...document.scripts].map((script) => script.src.match(BUNDLE_PATTERN)?.[0]).find(Boolean);
  if (!running) return false; // local development
  const response = await fetch(`/index.html?check=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) return false;
  const live = (await response.text()).match(BUNDLE_PATTERN)?.[0];
  return Boolean(live && live !== running);
};

// True while someone is typing, drawing or choosing something a reload would lose.
const isWorkInProgress = () => Boolean(document.querySelector('.pm-overlay, .fp-overlay, .fp-session, .shell-sheet-backdrop'));

// A home-screen app stays paused and resumes the old version, so it checks whenever it comes back.
const useNewVersion = () => {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (document.hidden || !navigator.onLine) return;
      try {
        if (!(await isNewVersionLive()) || cancelled) return;
        if (isWorkInProgress()) setAvailable(true);
        else window.location.reload();
      } catch { /* no connection: check again next time */ }
    };
    check();
    const timer = setInterval(check, VERSION_CHECK_EVERY_MS);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  return available;
};

const AppShell = ({ user, farmName, navItems, activePage, onNavigate, onLogout, children }) => {
  const newVersionAvailable = useNewVersion();
  const { language } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const capture = (event) => { event.preventDefault(); setInstallPrompt(event); };
    window.addEventListener('beforeinstallprompt', capture);
    return () => window.removeEventListener('beforeinstallprompt', capture);
  }, []);

  const shortLabel = (item) => getPage(item.id)?.short?.[language] || getPage(item.id)?.short?.ro || item.label;

  const shown = navItems.filter((item) => getPage(item.id)?.visible);
  const hidden = navItems.filter((item) => !getPage(item.id)?.visible);
  const tabs = shown.filter((item) => getPage(item.id)?.primary);
  const moreItems = shown.filter((item) => !getPage(item.id)?.primary);
  const moreIsActive = !tabs.some((item) => item.id === activePage);

  const go = (id) => { setMoreOpen(false); onNavigate(id); };

  const startInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      return;
    }
    setMoreOpen(false);
    setInstallOpen(true);
  };

  const canInstall = !isInstalled();

  return (
    <div className="shell">
      <nav className="shell-rail" aria-label="Meniu principal">
        <div className="shell-brand">
          <img src="/logo-farmtrackpro.svg" alt="" width="36" height="36" />
          <span>FarmTrack Pro</span>
        </div>
        <div className="shell-rail-items">
          {shown.map((item) => (
            <button key={item.id} type="button" onClick={() => go(item.id)}
              className={`shell-rail-item ${activePage === item.id ? 'is-active' : ''}`}
              aria-current={activePage === item.id ? 'page' : undefined}>
              <Icon name={item.id} /> {item.label}
            </button>
          ))}
          {hidden.length > 0 && <div className="shell-rail-group">Ascunse (doar local)</div>}
          {hidden.map((item) => (
            <button key={item.id} type="button" onClick={() => go(item.id)}
              className={`shell-rail-item is-hidden-page ${activePage === item.id ? 'is-active' : ''}`}>
              <Icon name={item.id} /> {shortLabel(item)}
            </button>
          ))}
        </div>
        <div className="shell-rail-foot">
          {canInstall && installPrompt && (
            <button type="button" className="shell-rail-item" onClick={startInstall}>
              <Icon name="install" /> Instalează aplicația
            </button>
          )}
          <div className="shell-user">
            <strong>{user?.name || 'Utilizator'}</strong>
            {farmName && <span>{farmName}</span>}
          </div>
          <button type="button" className="shell-rail-item" onClick={onLogout}>
            <Icon name="logout" /> Deconectare
          </button>
        </div>
      </nav>

      <main className="shell-content">{children}</main>

      {newVersionAvailable && (
        <div className="shell-update" role="status">
          <span>Versiune nouă disponibilă.</span>
          <button type="button" onClick={() => window.location.reload()}>Actualizează</button>
        </div>
      )}

      <nav className="shell-tabs" aria-label="Meniu principal">
        {tabs.map((item) => (
          <button key={item.id} type="button" onClick={() => go(item.id)}
            className={`shell-tab ${activePage === item.id ? 'is-active' : ''}`}
            aria-current={activePage === item.id ? 'page' : undefined}>
            <span className="shell-tab-icon"><Icon name={item.id} /></span>
            {shortLabel(item)}
          </button>
        ))}
        <button type="button" onClick={() => setMoreOpen(true)}
          className={`shell-tab ${moreIsActive ? 'is-active' : ''}`} aria-haspopup="dialog" aria-expanded={moreOpen}>
          <span className="shell-tab-icon"><Icon name="more" /></span>
          Mai mult
        </button>
      </nav>

      {moreOpen && (
        <div className="shell-sheet-backdrop" onClick={(event) => event.target === event.currentTarget && setMoreOpen(false)}>
          <div className="shell-sheet" role="dialog" aria-modal="true" aria-label="Mai mult">
            <div className="shell-sheet-head">
              <div>
                <strong>{user?.name || 'Utilizator'}</strong>
                {farmName && <span>{farmName}</span>}
              </div>
              <button type="button" className="shell-sheet-close" onClick={() => setMoreOpen(false)}>Închide</button>
            </div>
            <div className="shell-sheet-list">
              {moreItems.map((item) => (
                <button key={item.id} type="button" className="shell-sheet-row" onClick={() => go(item.id)}>
                  <Icon name={item.id} /> {item.label}
                </button>
              ))}
              {canInstall && (
                <button type="button" className="shell-sheet-row" onClick={startInstall}>
                  <Icon name="install" /> Instalează aplicația
                </button>
              )}
              <button type="button" className="shell-sheet-row is-danger" onClick={onLogout}>
                <Icon name="logout" /> Deconectare
              </button>
            </div>
            {hidden.length > 0 && (
              <>
                <div className="shell-sheet-group">Ascunse (doar local)</div>
                <div className="shell-sheet-list">
                  {hidden.map((item) => (
                    <button key={item.id} type="button" className="shell-sheet-row" onClick={() => go(item.id)}>
                      <Icon name={item.id} /> {shortLabel(item)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {installOpen && (
        <div className="shell-sheet-backdrop" onClick={(event) => event.target === event.currentTarget && setInstallOpen(false)}>
          <div className="shell-sheet" role="dialog" aria-modal="true" aria-label="Instalează aplicația">
            <div className="shell-sheet-head">
              <div><strong>Instalează aplicația</strong></div>
              <button type="button" className="shell-sheet-close" onClick={() => setInstallOpen(false)}>Închide</button>
            </div>
            {isIOS() ? (
              <ol className="shell-steps">
                <li>Deschide <strong>farmtrackpro.com</strong> în Safari.</li>
                <li>Apasă butonul <strong>Partajare</strong> din bara browserului.</li>
                <li>Alege <strong>Adaugă pe ecranul principal</strong>.</li>
                <li>Apasă <strong>Adaugă</strong>, apoi deschide aplicația de pe ecran și conectează-te.</li>
              </ol>
            ) : (
              <ol className="shell-steps">
                <li>Deschide meniul browserului (cele trei puncte).</li>
                <li>Alege <strong>Instalează aplicația</strong> sau <strong>Adaugă pe ecranul principal</strong>.</li>
                <li>Deschide aplicația de pe ecran și conectează-te.</li>
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
